<script lang="ts">
  import { MIN_EVENT_MINUTES } from "../../_shared/_logic/display-scale"
  import { onDestroy } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import BoardColumn from "./_modules/board-column"
  import ColumnHeader from "./_shared/_modules/column-header"
  import CalendarDragPreview from "./_modules/calendar-drag-preview"
  import BoardHorizontalScrollControls from "./_modules/board-horizontal-scroll-controls"
  import {
    computeEventPosition,
    computeMoveTimes,
    computeSoftMoveTimes,
    findCalendarAreaFromPoint,
    minutesFromDate,
    minutesFromPointer,
    minutesFromSoftTargetPointer,
    resolveTrueEventDurationMinutes,
    snapToStep,
  } from "./_shared/_logic/drag-resize"
  import type {
    BoardActions,
    BoardColumn as BoardColumnType,
    BoardEvent,
    BoardInteractionConfig,
    BoardLayoutConfig,
    BoardMoveInteraction,
    BoardMovePosition,
    BoardMovePreview,
    BoardMoveStartPayload,
    SoftSlotDisplayMode,
    TimeSlot,
  } from "../../../../_logic/calendar-types"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>
  type MoveDisplayMode = "same-column" | "cross-column" | "invalid"
  const SOFT_SLOT_OCCUPIED_MESSAGE = "Dieser Flexslot ist bereits vergeben."
  type CalendarDragPreviewHandle = {
    show: (interaction: BoardMoveInteraction, clientX: number, clientY: number) => void
    move: (clientX: number, clientY: number) => void
    hide: () => void
  }

  interface Props {
    columns: BoardColumnType[]
    timeColumnHeight: number
    timeSlots: TimeSlot[]
    layout: BoardLayoutConfig
    actions: BoardActions
    interaction: BoardInteractionConfig
    now: Date
    formatDayHeader: (day: Date) => string
    formatAddress: (property?: BoardPropertyRef) => string
    columnGap: number
    columnWidth: number
    showCancelReplan?: boolean
    cancelReplanColumnIds?: string[]
    readOnly?: boolean
    showEventTooltips?: boolean
    showAppointmentTagTooltip?: boolean
    softSlotDisplayMode?: SoftSlotDisplayMode
    isLoading?: boolean
    allowSlotCreation?: boolean
    allowBlockerCreation?: boolean
    editableEmployeeIds?: string[] | null
    appointmentCreationEmployeeIds?: string[] | null
    blockerCreationEmployeeIds?: string[] | null
    showUpdateSuccessNotification?: boolean
    stickyHeaderTop?: string
  }

  let {
    columns = [],
    timeColumnHeight = 0,
    timeSlots = [],
    layout,
    actions,
    interaction,
    now,
    formatDayHeader,
    formatAddress,
    columnGap,
    columnWidth,
    showCancelReplan = false,
    cancelReplanColumnIds,
    readOnly = false,
    showEventTooltips = true,
    showAppointmentTagTooltip = true,
    softSlotDisplayMode = "interactive",
    isLoading = false,
    allowSlotCreation = true,
    allowBlockerCreation = allowSlotCreation,
    editableEmployeeIds = null,
    appointmentCreationEmployeeIds = editableEmployeeIds,
    blockerCreationEmployeeIds = editableEmployeeIds,
    showUpdateSuccessNotification = true,
    stickyHeaderTop = "0px",
  }: Props = $props()

  let boardEl: HTMLDivElement | null = $state(null)
  let headerWindowEl: HTMLDivElement | null = $state(null)
  let windowEl: HTMLDivElement | null = $state(null)
  let columnWindowEl: HTMLDivElement | null = $state(null)
  let scrollLeft = $state(0)
  let maxScrollLeft = $state(0)
  let scrollContentWidth = $state(1)
  let moveInteraction = $state<BoardMoveInteraction | null>(null)
  let dragPreviewHandle: CalendarDragPreviewHandle | null = null
  let floatingPreviewInteraction: BoardMoveInteraction | null = null

  const parallelLaneWidth = 160
  const hasColumns = $derived(Boolean(columns.length))
  const totalMinutes = $derived(Math.max(0, (timeSlots.length - 1) * layout.slotMinutes))
  const scrollStep = $derived(columnWidth + columnGap)
  const columnTemplate = $derived.by(() =>
    columns
      .map((column) => {
        const extraLanes = Math.max(0, (column.parallelLaneCount ?? 1) - 1)

        return `${columnWidth + extraLanes * parallelLaneWidth}px`
      })
      .join(" "),
  )
  const canScrollLeft = $derived(scrollLeft > 1)
  const canScrollRight = $derived(scrollLeft < maxScrollLeft - 1)
  const hasHorizontalOverflow = $derived(maxScrollLeft > 1)
  const movingColumn = $derived.by(() => {
    if (!moveInteraction) return null

    return (
      columns.find(
        (column) =>
          column.id === (moveInteraction.currentColumnId ?? moveInteraction.sourceColumnId),
      ) ??
      columns.find((column) => column.id === moveInteraction.sourceColumnId) ??
      null
    )
  })
  const movingEmployeeLabel = $derived(movingColumn?.employee?.label ?? "")
  const movingCalendarSyncEnabled = $derived(Boolean(movingColumn?.calendarSyncEnabled))
  const employeeIsEditable = (employeeId: string | null | undefined): boolean =>
    editableEmployeeIds == null || Boolean(employeeId && editableEmployeeIds.includes(employeeId))
  const columnIsEditable = (column: BoardColumnType): boolean =>
    !readOnly && !isLoading && employeeIsEditable(column.employee?.id)
  const columnAllowsAppointmentCreation = (column: BoardColumnType): boolean =>
    !readOnly &&
    !isLoading &&
    (appointmentCreationEmployeeIds == null ||
      Boolean(column.employee?.id && appointmentCreationEmployeeIds.includes(column.employee.id)))
  const columnAllowsBlockerCreation = (column: BoardColumnType): boolean =>
    !readOnly &&
    !isLoading &&
    (blockerCreationEmployeeIds == null ||
      Boolean(column.employee?.id && blockerCreationEmployeeIds.includes(column.employee.id)))

  const updateScrollMetrics = () => {
    const el = windowEl

    if (!el) return

    const nextContentWidth = Math.max(1, el.scrollWidth)
    const nextMax = Math.max(0, el.scrollWidth - el.clientWidth)
    scrollContentWidth = nextContentWidth
    maxScrollLeft = nextMax

    if (el.scrollLeft > nextMax) {
      el.scrollLeft = nextMax
    }

    scrollLeft = Math.max(0, Math.min(nextMax, el.scrollLeft))

    if (headerWindowEl && Math.abs(headerWindowEl.scrollLeft - scrollLeft) > 1) {
      headerWindowEl.scrollLeft = scrollLeft
    }
  }

  const syncScroll = (source: "board" | "controls" | "header", value: number) => {
    const nextLeft = Math.max(0, Math.min(maxScrollLeft, value))
    scrollLeft = nextLeft

    if (source !== "board" && windowEl && Math.abs(windowEl.scrollLeft - nextLeft) > 1) {
      windowEl.scrollLeft = nextLeft
    }

    if (
      source !== "header" &&
      headerWindowEl &&
      Math.abs(headerWindowEl.scrollLeft - nextLeft) > 1
    ) {
      headerWindowEl.scrollLeft = nextLeft
    }
  }

  $effect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return

    const b = boardEl
    const h = headerWindowEl
    const w = windowEl
    const c = columnWindowEl
    updateScrollMetrics()
    const observer = new ResizeObserver(updateScrollMetrics)

    if (b) observer.observe(b)

    if (h) observer.observe(h)

    if (w) observer.observe(w)

    if (c) observer.observe(c)

    window.addEventListener("resize", updateScrollMetrics)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", updateScrollMetrics)
    }
  })

  $effect(() => {
    columns
    columnWidth
    columnGap
    columnTemplate

    if (typeof queueMicrotask === "function") {
      queueMicrotask(updateScrollMetrics)
    } else {
      updateScrollMetrics()
    }
  })

  const handleBoardScroll = () => {
    if (!windowEl) return

    updateScrollMetrics()
    syncScroll("board", windowEl.scrollLeft)
  }

  const handleHeaderScroll = () => {
    if (!headerWindowEl) return

    syncScroll("header", headerWindowEl.scrollLeft)
  }

  const handleStep = (direction: number) => {
    syncScroll("controls", scrollLeft + direction * scrollStep)
  }

  const formatTime = (value: Date) =>
    value.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })

  const parseEventDate = (value: string | null | undefined, fallback: Date) => {
    if (!value) return fallback

    const parsed = new Date(value)

    return Number.isNaN(parsed.getTime()) ? fallback : parsed
  }

  const datesEqual = (left: Date | null | undefined, right: Date | null | undefined) =>
    (left?.getTime?.() ?? null) === (right?.getTime?.() ?? null)

  const startOfDayTs = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  const todayStartTs = $derived(startOfDayTs(now))
  const isPastColumnDay = (column: BoardColumnType) => startOfDayTs(column.day) < todayStartTs

  const clonePreviewItem = ({
    source,
    start,
    end,
    column,
    top,
    height,
    targetKind,
    timeframeStart,
    timeframeEnd,
  }: {
    source: BoardEvent
    start: Date
    end: Date
    column: BoardColumnType
    top: number
    height: number
    targetKind: "hard" | "soft"
    timeframeStart: Date | null
    timeframeEnd: Date | null
  }): BoardEvent => {
    const employeeId = column.employee?.id ?? null
    const employeeName = column.employee?.label ?? source.event.employee_name ?? null
    const event: ScheduleEvent = {
      ...source.event,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      employee_id: employeeId,
      employee_name: employeeName,
      flex_type: targetKind === "soft" ? "SOFT" : "HARD",
    }

    if (targetKind === "soft") {
      event.timeframe_start_at = (timeframeStart ?? start).toISOString()
      event.timeframe_end_at = (timeframeEnd ?? end).toISOString()
    } else {
      event.timeframe_start_at = start.toISOString()
      event.timeframe_end_at = end.toISOString()
    }

    return {
      ...source,
      event,
      start,
      end,
      top,
      height,
      timeLabel: `${formatTime(start)} - ${formatTime(end)}`,
      isPast: false,
    }
  }

  const displayModeForMove = (current: BoardMoveInteraction | null): MoveDisplayMode | null => {
    if (!current) return null

    if (!current.validDrop) return "invalid"

    return current.currentColumnId === current.sourceColumnId &&
      current.targetKind === "hard" &&
      current.sourceKind === "hard"
      ? "same-column"
      : "cross-column"
  }

  const positionForMove = (current: BoardMoveInteraction, column: BoardColumnType) =>
    computeEventPosition({
      start: current.currentStart,
      end: current.currentEnd,
      day: column.day,
      layout,
      totalMinutes,
    })

  const buildActiveMovePositionForColumn = (column: BoardColumnType): BoardMovePosition | null => {
    const current = moveInteraction

    if (
      !current?.validDrop ||
      current.currentColumnId !== column.id ||
      current.targetKind !== "hard"
    )
      return null

    const position = positionForMove(current, column)

    return {
      eventId: current.item.event.id,
      targetKind: current.targetKind,
      top: position.top,
      height: position.height,
      currentStart: current.currentStart,
      currentEnd: current.currentEnd,
    }
  }

  const buildHiddenEventIdForColumn = (column: BoardColumnType) => {
    const current = moveInteraction
    const displayMode = displayModeForMove(current)

    if (!current || current.sourceColumnId !== column.id || displayMode === "same-column")
      return null

    return current.item.event.id
  }

  const buildMovePreviewForColumn = (column: BoardColumnType): BoardMovePreview | null => {
    const current = moveInteraction

    if (
      !current?.validDrop ||
      current.currentColumnId !== column.id ||
      displayModeForMove(current) === "same-column"
    ) {
      return null
    }

    const position = positionForMove(current, column)

    return {
      item: clonePreviewItem({
        source: current.item,
        start: current.currentStart,
        end: current.currentEnd,
        column,
        top: position.top,
        height: position.height,
        targetKind: current.targetKind,
        timeframeStart: current.targetTimeframeStart,
        timeframeEnd: current.targetTimeframeEnd,
      }),
      targetKind: current.targetKind,
      top: position.top,
      height: position.height,
      columnId: column.id,
      currentStart: current.currentStart,
      currentEnd: current.currentEnd,
      validDrop: true,
    }
  }

  const withInvalidMoveTarget = (
    current: BoardMoveInteraction,
    target: Partial<
      Pick<
        BoardMoveInteraction,
        | "currentColumnId"
        | "targetDay"
        | "targetEmployeeId"
        | "targetKind"
        | "targetTimeframeStart"
        | "targetTimeframeEnd"
        | "invalidReason"
      >
    > = {},
  ): BoardMoveInteraction => ({
    ...current,
    ...target,
    validDrop: false,
    invalidReason: target.invalidReason ?? null,
  })

  const moveInteractionFromPointer = (
    current: BoardMoveInteraction,
    event: PointerEvent,
  ): BoardMoveInteraction => {
    const targetArea = findCalendarAreaFromPoint(event.clientX, event.clientY)

    if (!targetArea) {
      return withInvalidMoveTarget(current, { currentColumnId: null })
    }

    if (!employeeIsEditable(targetArea.employeeId)) {
      return withInvalidMoveTarget(current, {
        currentColumnId: targetArea.columnId,
        targetDay: targetArea.day,
        targetEmployeeId: targetArea.employeeId,
        invalidReason: "Dieser Mitarbeiterbereich ist schreibgeschützt.",
      })
    }

    if (
      interaction.canMoveEventToEmployee &&
      !interaction.canMoveEventToEmployee(current.item, targetArea.employeeId)
    ) {
      return withInvalidMoveTarget(current, {
        currentColumnId: targetArea.columnId,
        targetDay: targetArea.day,
        targetEmployeeId: targetArea.employeeId,
        invalidReason:
          "Dieser freie Kalenderblock darf nur dem eigenen Arbeitsbereich zugeordnet werden.",
      })
    }

    const minutesInArea =
      targetArea.kind === "soft" && targetArea.timeframeStart
        ? minutesFromSoftTargetPointer({
            clientY: event.clientY,
            area: targetArea.area,
            targetDay: targetArea.day,
            timeframeStart: targetArea.timeframeStart,
            layout,
          })
        : minutesFromPointer({
            clientY: event.clientY,
            area: targetArea.area,
            slotHeight: layout.slotHeight,
            slotMinutes: layout.slotMinutes,
            slotCount: timeSlots.length - 1,
          })

    if (minutesInArea == null) {
      return withInvalidMoveTarget(current, {
        currentColumnId: targetArea.columnId,
        targetDay: targetArea.day,
        targetEmployeeId: targetArea.employeeId,
        targetKind: targetArea.kind,
        targetTimeframeStart: targetArea.timeframeStart,
        targetTimeframeEnd: targetArea.timeframeEnd,
      })
    }

    if (
      targetArea.kind === "soft" &&
      targetArea.softSlotOccupiedEventId &&
      targetArea.softSlotOccupiedEventId !== current.item.event.id
    ) {
      return withInvalidMoveTarget(current, {
        currentColumnId: targetArea.columnId,
        targetDay: targetArea.day,
        targetEmployeeId: targetArea.employeeId,
        targetKind: targetArea.kind,
        targetTimeframeStart: targetArea.timeframeStart,
        targetTimeframeEnd: targetArea.timeframeEnd,
        invalidReason: SOFT_SLOT_OCCUPIED_MESSAGE,
      })
    }

    const nextTimes =
      targetArea.kind === "soft" && targetArea.timeframeStart && targetArea.timeframeEnd
        ? computeSoftMoveTimes({
            pointerMinutes: minutesInArea,
            targetDay: targetArea.day,
            offsetMinutes: current.offsetMinutes,
            durationMinutes: current.durationMinutes,
            timeframeStart: targetArea.timeframeStart,
            timeframeEnd: targetArea.timeframeEnd,
            layout,
          })
        : {
            ...computeMoveTimes({
              pointerMinutes: minutesInArea,
              targetDay: targetArea.day,
              offsetMinutes: current.offsetMinutes,
              durationMinutes: current.durationMinutes,
              layout,
              totalMinutes,
            }),
            timeframeStart: null,
            timeframeEnd: null,
          }

    return {
      ...current,
      currentStart: nextTimes.start,
      currentEnd: nextTimes.end,
      targetDay: targetArea.day,
      targetEmployeeId: targetArea.employeeId,
      currentColumnId: targetArea.columnId,
      targetKind: targetArea.kind,
      targetTimeframeStart: targetArea.kind === "soft" ? nextTimes.timeframeStart : nextTimes.start,
      targetTimeframeEnd: targetArea.kind === "soft" ? nextTimes.timeframeEnd : nextTimes.end,
      validDrop: true,
      invalidReason: null,
    }
  }

  const moveInteractionsMatch = (
    left: BoardMoveInteraction | null,
    right: BoardMoveInteraction | null,
  ) => {
    if (left === right) return true

    if (!left || !right) return false

    return (
      left.validDrop === right.validDrop &&
      left.currentColumnId === right.currentColumnId &&
      left.targetEmployeeId === right.targetEmployeeId &&
      left.targetKind === right.targetKind &&
      left.invalidReason === right.invalidReason &&
      datesEqual(left.targetTimeframeStart, right.targetTimeframeStart) &&
      datesEqual(left.targetTimeframeEnd, right.targetTimeframeEnd) &&
      left.targetDay.getTime() === right.targetDay.getTime() &&
      left.currentStart.getTime() === right.currentStart.getTime() &&
      left.currentEnd.getTime() === right.currentEnd.getTime()
    )
  }

  const shouldShowFloatingPreview = (current: BoardMoveInteraction | null) => {
    const displayMode = displayModeForMove(current)

    return displayMode === "cross-column" || displayMode === "invalid"
  }

  const setDragPreviewHandle = (handle: CalendarDragPreviewHandle | null) => {
    dragPreviewHandle = handle
  }

  const hideFloatingPreview = () => {
    floatingPreviewInteraction = null
    dragPreviewHandle?.hide()
  }

  const notifyCalendarChangeSaved = () => {
    newNotification({ class: "success", html: "Kalenderänderung gespeichert." })
  }

  const syncFloatingPreview = (
    current: BoardMoveInteraction,
    event: PointerEvent,
    forceRefresh = false,
  ) => {
    if (!shouldShowFloatingPreview(current)) {
      hideFloatingPreview()

      return
    }

    if (forceRefresh || !moveInteractionsMatch(floatingPreviewInteraction, current)) {
      dragPreviewHandle?.show(current, event.clientX, event.clientY)
      floatingPreviewInteraction = current

      return
    }

    dragPreviewHandle?.move(event.clientX, event.clientY)
  }

  const applyMoveInteraction = (
    nextInteraction: BoardMoveInteraction,
    event: PointerEvent,
    forceRefresh = false,
  ) => {
    const shouldRefreshState =
      forceRefresh || !moveInteractionsMatch(moveInteraction, nextInteraction)

    if (shouldRefreshState) {
      moveInteraction = nextInteraction
    }

    syncFloatingPreview(nextInteraction, event, shouldRefreshState)
  }

  const edgeScrollDuringMove = (clientX: number) => {
    const el = windowEl

    if (!el || maxScrollLeft <= 0) return

    const rect = el.getBoundingClientRect()
    const threshold = 72
    const step = 28
    const delta =
      clientX < rect.left + threshold ? -step : clientX > rect.right - threshold ? step : 0

    if (!delta) return

    const nextLeft = Math.max(0, Math.min(maxScrollLeft, el.scrollLeft + delta))

    if (Math.abs(nextLeft - el.scrollLeft) < 1) return

    el.scrollLeft = nextLeft
    updateScrollMetrics()
    syncScroll("board", el.scrollLeft)
  }

  const clearMoveListeners = () => {
    window.removeEventListener("pointermove", handleMovePointerMove)
    window.removeEventListener("pointerup", finishMoveInteraction)
    window.removeEventListener("pointercancel", cancelMoveInteraction)
  }

  const startMoveInteraction = (payload: BoardMoveStartPayload) => {
    const { item, pointer, column, area, sourceRect } = payload

    if (moveInteraction) return

    pointer.preventDefault()
    pointer.stopPropagation()

    const pointerMinutes =
      minutesFromPointer({
        clientY: pointer.clientY,
        area,
        slotHeight: layout.slotHeight,
        slotMinutes: layout.slotMinutes,
        slotCount: timeSlots.length - 1,
      }) ?? minutesFromDate(item.start, column.day, layout.dayStartHour)
    const itemStartMinutes = minutesFromDate(item.start, column.day, layout.dayStartHour)
    const sourceKind =
      payload.sourceKind ??
      ((item.event.flex_type as ScheduleEventFlexType) === "SOFT" ? "soft" : "hard")
    const concreteStart = parseEventDate(item.event.start_at, item.start)
    const concreteEnd = parseEventDate(item.event.end_at, item.end)
    const durationBase =
      sourceKind === "soft" ? { ...item, start: concreteStart, end: concreteEnd } : item
    const durationMinutes = resolveTrueEventDurationMinutes(durationBase, MIN_EVENT_MINUTES)
    const originalStart = sourceKind === "soft" ? concreteStart : item.start
    const originalEnd =
      sourceKind === "soft"
        ? new Date(originalStart.getTime() + durationMinutes * 60 * 1000)
        : item.end
    const previewHeight = Math.max(
      layout.minEventHeight,
      (durationMinutes / layout.slotMinutes) * layout.slotHeight,
    )
    const adjustedSourceRect = {
      ...sourceRect,
      height: previewHeight,
      pointerOffsetY: Math.max(0, Math.min(previewHeight, sourceRect.pointerOffsetY)),
    }
    const maxOffset = Math.max(0, durationMinutes - MIN_EVENT_MINUTES)
    const offsetMinutes = Math.max(
      0,
      Math.min(maxOffset, snapToStep(pointerMinutes - itemStartMinutes, layout.slotMinutes)),
    )
    const baseInteraction: BoardMoveInteraction = {
      item,
      pointerId: pointer.pointerId,
      sourceColumnId: column.id,
      sourceEmployeeId: column.employee?.id ?? null,
      sourceKind,
      sourceRect: adjustedSourceRect,
      offsetMinutes,
      durationMinutes,
      originalStart,
      originalEnd,
      currentStart: originalStart,
      currentEnd: originalEnd,
      targetDay: column.day,
      targetEmployeeId: column.employee?.id ?? null,
      targetKind: sourceKind,
      targetTimeframeStart:
        sourceKind === "soft"
          ? parseEventDate(item.event.timeframe_start_at, item.start)
          : originalStart,
      targetTimeframeEnd:
        sourceKind === "soft" ? parseEventDate(item.event.timeframe_end_at, item.end) : originalEnd,
      currentColumnId: column.id,
      validDrop: true,
      invalidReason: null,
    }
    applyMoveInteraction(moveInteractionFromPointer(baseInteraction, pointer), pointer, true)

    window.addEventListener("pointermove", handleMovePointerMove)
    window.addEventListener("pointerup", finishMoveInteraction)
    window.addEventListener("pointercancel", cancelMoveInteraction)
  }

  const handleMovePointerMove = (event: PointerEvent) => {
    const current = moveInteraction

    if (!current || event.pointerId !== current.pointerId) return

    edgeScrollDuringMove(event.clientX)
    applyMoveInteraction(moveInteractionFromPointer(current, event), event)
  }

  const cancelMoveInteraction = (event?: PointerEvent) => {
    if (event && moveInteraction && event.pointerId !== moveInteraction.pointerId) return

    clearMoveListeners()
    hideFloatingPreview()
    moveInteraction = null
  }

  const finishMoveInteraction = (event: PointerEvent) => {
    const current = moveInteraction

    if (!current || event.pointerId !== current.pointerId) return

    const finalInteraction = moveInteractionFromPointer(current, event)
    clearMoveListeners()
    hideFloatingPreview()

    if (!finalInteraction.validDrop) {
      if (finalInteraction.invalidReason) {
        newNotification({ class: "error", html: finalInteraction.invalidReason })
      }

      moveInteraction = null

      return
    }

    const start = finalInteraction.currentStart
    const end = finalInteraction.currentEnd
    const employeeId =
      finalInteraction.targetEmployeeId ?? finalInteraction.sourceEmployeeId ?? null
    const flexType: ScheduleEventFlexType = finalInteraction.targetKind === "soft" ? "SOFT" : "HARD"
    const timeframeStart =
      finalInteraction.targetKind === "soft" ? finalInteraction.targetTimeframeStart : start
    const timeframeEnd =
      finalInteraction.targetKind === "soft" ? finalInteraction.targetTimeframeEnd : end
    const currentTimeframeStart = parseEventDate(
      finalInteraction.item.event.timeframe_start_at,
      finalInteraction.originalStart,
    )
    const currentTimeframeEnd = parseEventDate(
      finalInteraction.item.event.timeframe_end_at,
      finalInteraction.originalEnd,
    )
    const changed =
      start.getTime() !== finalInteraction.originalStart.getTime() ||
      end.getTime() !== finalInteraction.originalEnd.getTime() ||
      employeeId !== (finalInteraction.item.event.employee_id ?? null) ||
      flexType !== (finalInteraction.item.event.flex_type as ScheduleEventFlexType) ||
      !datesEqual(timeframeStart, currentTimeframeStart) ||
      !datesEqual(timeframeEnd, currentTimeframeEnd)

    moveInteraction = null

    if (!changed) return

    try {
      const result = actions.updateEvent({
        event: finalInteraction.item.event,
        start,
        end,
        employeeId,
        flexType,
        timeframeStart,
        timeframeEnd,
      })
      void Promise.resolve(result)
        .then((accepted) => {
          if (accepted !== false && showUpdateSuccessNotification) notifyCalendarChangeSaved()
        })
        .catch((err) => {
          console.error("calendar_move_interaction_update_failed", err)
          newNotification({
            class: "error",
            html: "Änderung konnte nicht übernommen werden. Bitte Seite aktualisieren und erneut versuchen.",
          })
        })
    } catch (err) {
      console.error("calendar_move_interaction_update_failed", err)
      newNotification({
        class: "error",
        html: "Änderung konnte nicht übernommen werden. Bitte Seite aktualisieren und erneut versuchen.",
      })
    }
  }

  onDestroy(() => {
    clearMoveListeners()
    hideFloatingPreview()
  })
</script>

<div class="calendar-board" style={`--time-height:${timeColumnHeight}px;`} bind:this={boardEl}>
  {#if !hasColumns}
    <div class="calendar-empty">
      <p>Keine Mitarbeitenden ausgewählt.</p>
    </div>
  {:else}
    <div
      class="board-sticky-header-stack"
      style={`--calendar-sticky-header-top:${stickyHeaderTop};`}
    >
      {#if hasHorizontalOverflow}
        <BoardHorizontalScrollControls
          placement="top"
          contentWidth={scrollContentWidth}
          {scrollLeft}
          {canScrollLeft}
          {canScrollRight}
          onScrollTo={(value) => syncScroll("controls", value)}
          onStep={handleStep}
        />
      {/if}
      <div
        class="board-header-scroll-window"
        class:with-horizontal-scrollbars={hasHorizontalOverflow}
        bind:this={headerWindowEl}
        onscroll={handleHeaderScroll}
      >
        <div
          class="column-window board-header-window"
          style={`--column-template:${columnTemplate};--column-gap:${columnGap}px;`}
        >
          {#each columns as column, index (column.id)}
            {@const columnEditable = columnIsEditable(column)}
            {@const appointmentCreationAllowed = columnAllowsAppointmentCreation(column)}
            {@const blockerCreationAllowed = columnAllowsBlockerCreation(column)}
            <div
              class="board-header-column"
              class:body-divider={index > 0}
              style={`--column-accent:${column.color ?? "var(--kontext-accent)"}`}
            >
              <ColumnHeader
                {column}
                {formatDayHeader}
                showCancelReplan={columnEditable && showCancelReplan}
                cancelReplanDisabled={cancelReplanColumnIds
                  ? !cancelReplanColumnIds.includes(column.id)
                  : false}
                isPastDay={isPastColumnDay(column)}
                {actions}
                readOnly={false}
                allowSlotCreation={allowSlotCreation && appointmentCreationAllowed}
                allowBlockerCreation={allowBlockerCreation && blockerCreationAllowed}
              />
            </div>
          {/each}
        </div>
      </div>
    </div>
    <div class="column-window-wrap">
      <div
        class="board-scroll-window"
        class:with-horizontal-scrollbars={hasHorizontalOverflow}
        bind:this={windowEl}
        onscroll={handleBoardScroll}
      >
        <div
          class="column-window"
          bind:this={columnWindowEl}
          style={`--column-template:${columnTemplate};--column-gap:${columnGap}px;`}
        >
          {#each columns as column, index (column.id)}
            {@const columnEditable = columnIsEditable(column)}
            {@const appointmentCreationAllowed = columnAllowsAppointmentCreation(column)}
            {@const blockerCreationAllowed = columnAllowsBlockerCreation(column)}
            <BoardColumn
              {column}
              {timeSlots}
              {layout}
              {actions}
              interaction={{
                ...interaction,
                canDirectlyEditEvents: columnEditable && interaction.canDirectlyEditEvents,
              }}
              {now}
              {formatDayHeader}
              {formatAddress}
              showCancelReplan={columnEditable && showCancelReplan}
              cancelReplanDisabled={cancelReplanColumnIds
                ? !cancelReplanColumnIds.includes(column.id)
                : false}
              showBodyDivider={index > 0}
              readOnly={false}
              {showEventTooltips}
              {showAppointmentTagTooltip}
              {softSlotDisplayMode}
              {isLoading}
              allowSlotCreation={allowSlotCreation && appointmentCreationAllowed}
              allowBlockerCreation={allowBlockerCreation && blockerCreationAllowed}
              {showUpdateSuccessNotification}
              showHeader={false}
              hiddenEventId={buildHiddenEventIdForColumn(column)}
              activeMovePosition={buildActiveMovePositionForColumn(column)}
              movePreview={buildMovePreviewForColumn(column)}
              onMoveStart={startMoveInteraction}
            />
          {/each}
        </div>
      </div>
    </div>
    {#if hasHorizontalOverflow}
      <BoardHorizontalScrollControls
        placement="bottom"
        contentWidth={scrollContentWidth}
        {scrollLeft}
        {canScrollLeft}
        {canScrollRight}
        onScrollTo={(value) => syncScroll("controls", value)}
        onStep={handleStep}
      />
    {/if}
  {/if}
  <CalendarDragPreview
    register={setDragPreviewHandle}
    {formatAddress}
    employeeLabel={movingEmployeeLabel}
    calendarSyncEnabled={movingCalendarSyncEnabled}
  />
</div>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .calendar-board {
    --cal-bg: #ffffff;
    --cal-surface: #eceaea;
    --cal-surface-soft: #f8f6f6;
    --cal-border: rgba(0, 0, 0, 0.06);
    --cal-border-strong: rgba(0, 0, 0, 0.12);
    --cal-text: #2f4858;
    --cal-text-muted: #617481;
    --cal-text-soft: #741e20;
    --cal-accent: #741e20;
    --cal-accent-soft: #ad514c;
    --cal-shadow: 0 18px 36px rgba(47, 72, 88, 0.12);
    --cal-scrollbar-surface: var(--cal-surface);
    --cal-scrollbar-track: rgba(13, 12, 12, 0.42);
    --cal-scrollbar-thumb: #0d0c0c;
    --kontext-accent: #00c95f;
    --primary-100: #741e20;
    --primary-200: #ad514c;
    --text-300: #2f4858;
    --success-100: #0f8f4d;
    --warning-100: #d97706;
    --error-100: #dc2626;
    --error-200: #b91c1c;

    display: flex;
    flex-direction: column;
    gap: 0;
    overflow: visible;
    margin-top: 0;

    .calendar-empty {
      padding: 1rem;
      border: 1px dashed var(--cal-border);
      border-radius: 8px;
      color: var(--cal-text-muted);
      background: var(--cal-bg);
    }

    @media @mobile {
      position: relative;
      flex-direction: column;
      overflow: hidden;

      :global(.hidden) {
        display: none;
      }
    }
  }

  .column-window-wrap {
    position: relative;
  }

  .board-sticky-header-stack {
    position: sticky;
    top: var(--calendar-sticky-header-top, 0px);
    z-index: var(--calendar-sticky-header-z-index, 40);
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    background: var(--cal-surface);
  }

  .board-header-scroll-window {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid var(--cal-border);
    border-bottom: 0;
    background: var(--cal-bg);
    box-shadow: 0 8px 18px rgba(47, 72, 88, 0.08);
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .board-header-column {
    background: var(--cal-bg, #ffffff);
    min-width: var(--column-min, 280px);
    width: 100%;
    box-sizing: border-box;

    &.body-divider {
      border-left: 1px solid var(--cal-border, rgba(0, 0, 0, 0.06));
    }

    :global(.column-header) {
      position: static;
      top: auto;
      height: 100%;
      box-shadow: none;
    }
  }

  .board-scroll-window {
    border: 1px solid var(--cal-border);
    border-top: 0;
    background-color: var(--cal-bg);
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: visible;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &.with-horizontal-scrollbars {
      border-top: 0;
      border-bottom: 0;
    }
  }

  .column-window {
    display: grid;
    grid-template-columns: var(--column-template, 360px);
    column-gap: var(--column-gap, 0px);
    row-gap: 0;
    width: max-content;
    min-width: max-content;
  }
</style>
